/**
 * UI action config aligned with backend canonical spec.
 * Source of truth: supported_ui_actions.json (keep in sync with chat-service/supported_ui_actions.json per docs/SSE_EVENT.md).
 * Use for: (a) display label per type, (b) handler (chat vs direct), (c) prompt to send when user clicks a chat action.
 */

import supportedUiActions from "./supported_ui_actions.json";
import type { UIAction } from "../types";

export type ActionHandlerType = "chat" | "direct";

export interface ActionConfigItem {
  defaultLabel: string;
  handler: ActionHandlerType;
  /** Message sent to chat when user clicks (handler === "chat"); null/empty for direct. */
  prompt: string;
}

export interface SupportedUIActionEntry {
  type: string;
  label: string;
  handler: "chat" | "direct";
  prompt: string | null;
}

/** Build config map from canonical JSON. Prefer backend-provided label in events when present. */
const actionsArray = (supportedUiActions as { actions: SupportedUIActionEntry[] }).actions;

export const ACTION_CONFIG: Record<string, ActionConfigItem> = actionsArray.reduce(
  (acc, entry) => {
    acc[entry.type] = {
      defaultLabel: entry.label,
      handler: entry.handler,
      prompt: entry.prompt ?? "",
    };
    return acc;
  },
  {} as Record<string, ActionConfigItem>
);

/** Default starter action types when no messages yet; labels come from supported_ui_actions.json. */
const STARTER_TYPES = [
  "create_posts_all_platforms",
  "research_topic",
] as const;

export const DEFAULT_STARTER_ACTIONS = STARTER_TYPES.map((type) => {
  const config = ACTION_CONFIG[type];
  return { type, label: config?.defaultLabel ?? type };
});

/**
 * Frontend-only starters for SMS/email communication agents (not content/social
 * workflows) — shown before the backend's start event arrives. check_availability
 * mirrors the backend default in chat-service/app/starter_actions.py
 * (_DEFAULTS["communication_agent_sms"/"communication_agent_email"]) so the
 * chips don't change out from under the user once the real start event lands
 * (SGL-123 — replaces the old "Test response" simulated-customer-message card).
 *
 * KEEP THESE TWO LISTS IDENTICAL — same `type` AND same `label`. They had
 * drifted apart (different labels and two types that exist in no other file),
 * which made the chips visibly change the moment the start event arrived.
 * Every `type` used here must also exist in
 * ai-agent-management/chat-service/supported_ui_actions.json, or the backend
 * cannot send it and resolveActionTypeFromLabel below cannot resolve it.
 */
export const COMMUNICATION_AGENT_STARTER_ACTIONS: UIAction[] = [
  {
    type: "check_availability",
    label: "Check my calendar availability",
    message: "Check my calendar availability",
  },
  {
    type: "book_appointment",
    label: "Book an appointment for me",
    message: "Book an appointment for me",
  },
  {
    type: "learn_more",
    label: "Summarize my inbox",
    message: "Summarize my inbox",
  },
];

/**
 * Frontend-only starters for the General Assistant (no fixed role — not a
 * content/social workflow). Mirrors the backend default in
 * chat-service/app/starter_actions.py so the chips shown before the
 * backend's start event arrives match what loads afterward.
 */
export const GENERAL_AGENT_STARTER_ACTIONS: UIAction[] = [
  // NewChatWindow swaps items 0 and 1 for visual balance, so "Explain..." is
  // second here to display first. All 3 render as cards.
  { type: "research_topic", label: "Research a topic for me" },
  { type: "learn_more", label: "Explain a topic in simple terms" },
  { type: "refine_ideas", label: "Brainstorm ideas with me" },
];

/** A chip whose label is the owner's own answer (SGL-421): sent as written, never
 *  matched to another action by its words ("Regenerate it shorter" is a reply). */
export const REPLY_ACTION_TYPE = "reply";

/** The action's type: a reply chip as sent, anything else resolved from its label. */
export function resolveActionType(action: Pick<UIAction, "type" | "label">): string {
  if (action.type === REPLY_ACTION_TYPE) return REPLY_ACTION_TYPE;
  return resolveActionTypeFromLabel(action.label) ?? action.type;
}

/**
 * Resolve action type from label when backend sends wrong or same type for all.
 * Uses canonical labels from supported_ui_actions.json.
 */
export function resolveActionTypeFromLabel(label: string | undefined): string | null {
  if (!label || typeof label !== "string") return null;
  const normalized = label.trim().toLowerCase();
  if (!normalized) return null;
  let fallback: string | null = null;
  for (const [type, config] of Object.entries(ACTION_CONFIG)) {
    const defaultLabel = (config.defaultLabel || "").trim().toLowerCase();
    if (!defaultLabel) continue;
    if (defaultLabel === normalized) return type;
    if (defaultLabel.startsWith(normalized) || normalized.startsWith(defaultLabel)) {
      if (!fallback) fallback = type;
    }
  }
  return fallback;
}
